import { useRef, useState } from 'react';
import { useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { sqlImportSyncTargetKind } from '@/features/sql-sync';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import type { SqlDatabaseType, SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import {
  useCreateSqlImportDatabase,
  useDeleteSqlImportDatabase,
  useUpdateSqlImportDatabase,
} from '@/services/db/sql-import-database/mutations';
import {
  databaseTitle,
  databaseWriteInput,
  parseDatabaseSaveError,
  transformSqlImportError,
  type DatabaseFormField,
  type DatabaseFormValues,
} from './database-form';
import type { SqlImportDraft } from './draft';
import { hasDatabaseDetails } from './steps';
import { DeleteDatabaseMessage } from './components/delete-database-message';

/** The form is open for a new database, or over a saved one. */
export type DatabaseEditor = { mode: 'add' } | { mode: 'edit'; database: SqlImportDatabase };

export type DatabaseSaveResult =
  | { status: 'saved' }
  /** Field errors from the backend; the form shows them under their fields. */
  | { status: 'invalid'; errors: { field: DatabaseFormField; message: string }[] }
  /** Anything else was reported outside the form (banner or snackbar). */
  | { status: 'failed' };

const SAVE_FAILED_MESSAGE = "Your database couldn't be saved.";
const DELETE_FAILED_MESSAGE = "Your database couldn't be deleted.";

/**
 * Where the step starts: on the form when there is nothing to pick from, on the single saved
 * database's form when it is missing settings, and otherwise on the list.
 */
export function initialDatabaseEditor(databases: readonly SqlImportDatabase[]): DatabaseEditor | null {
  if (databases.length === 0) {
    return { mode: 'add' };
  }
  const [only] = databases;
  if (databases.length === 1 && only != null && !hasDatabaseDetails(only)) {
    return { mode: 'edit', database: only };
  }
  return null;
}

type UseDatabaseStepArgs = {
  workspaceId: number;
  type: SqlDatabaseType;
  /** The engine's product name, for analytics. */
  databaseTypeName: string;
  syncTarget: string;
  databases: readonly SqlImportDatabase[];
  /** The database the draft points at. */
  selectedId: string | undefined;
  onSelect: (id: string | undefined) => void;
};

/**
 * The Database step's state: which database the draft uses, whether the connection form is open
 * and for what, and the saves and deletes behind it. The component is layout only.
 */
export function useDatabaseStep({
  workspaceId,
  type,
  databaseTypeName,
  syncTarget,
  databases,
  selectedId,
  onSelect,
}: UseDatabaseStepArgs) {
  const { track } = useAnalytics();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const [editor, setEditor] = useState<DatabaseEditor | null>(() => initialDatabaseEditor(databases));
  /** The backend could not connect with the last submitted settings. */
  const [connectionError, setConnectionError] = useState<string | null>(null);

  /** A save is in flight; a second submit before it lands is dropped rather than duplicated. */
  const saveInFlight = useRef(false);
  const create = useCreateSqlImportDatabase({ workspaceId });
  const update = useUpdateSqlImportDatabase({ workspaceId });
  const remove = useDeleteSqlImportDatabase({ workspaceId });

  // The draft's `database_id` is the whole story: `withDefaultDatabase` has already recorded a lone
  // saved database on it, so what renders as selected is what every request carries.
  const selected = databases.find((database) => database.id === selectedId);
  const eventProperties = { 'Database Type': databaseTypeName, 'Sync Target': sqlImportSyncTargetKind(syncTarget) };

  const openEditor = (next: DatabaseEditor) => {
    setConnectionError(null);
    setEditor(next);
  };

  /** Sort a failed save into what the form, a banner, or a snackbar should show. */
  const reportSaveFailure = (error: unknown, mode: DatabaseEditor['mode']): DatabaseSaveResult => {
    const failure = parseDatabaseSaveError(error);
    const detail =
      failure.kind === 'connection' ? failure.detail : getUserFacingErrorMessage(error, SAVE_FAILED_MESSAGE);
    void track('sqlImports', mode === 'add' ? 'addDatabaseFailed' : 'editDatabaseFailed', {
      ...eventProperties,
      'Error Message': detail,
    });
    switch (failure.kind) {
      case 'connection':
        setConnectionError(transformSqlImportError(failure.detail));
        return { status: 'failed' };
      case 'fields':
        return { status: 'invalid', errors: failure.errors };
      case 'other':
        snackbars.create({ id: 'sqlImports.saveDatabase.error', variant: 'critical', message: detail });
        return { status: 'failed' };
      default: {
        const _exhaustive: never = failure;
        return _exhaustive;
      }
    }
  };

  const saveDatabase = async (values: DatabaseFormValues): Promise<DatabaseSaveResult> => {
    if (editor == null || saveInFlight.current) {
      return { status: 'failed' };
    }
    saveInFlight.current = true;
    // Only the current attempt's failure stays on screen.
    setConnectionError(null);
    const write = databaseWriteInput(type, values);
    try {
      const saved =
        editor.mode === 'add'
          ? await create.mutateAsync(write)
          : await update.mutateAsync({ databaseId: editor.database.id, ...write });
      onSelect(saved.id);
      setEditor(null);
      return { status: 'saved' };
    } catch (error) {
      return reportSaveFailure(error, editor.mode);
    } finally {
      saveInFlight.current = false;
    }
  };

  const deleteDatabase = async (database: SqlImportDatabase) => {
    void track('sqlImports', 'deleteDatabaseStarted', eventProperties);
    try {
      await remove.mutateAsync({ databaseId: database.id });
    } catch (error) {
      snackbars.create({
        id: 'sqlImports.deleteDatabase.error',
        variant: 'critical',
        message: getUserFacingErrorMessage(error, DELETE_FAILED_MESSAGE),
      });
      return;
    }
    if (selectedId === database.id) {
      onSelect(undefined);
    }
    if (databases.length === 1) {
      openEditor({ mode: 'add' });
    }
  };

  return {
    editor,
    connectionError,
    isSaving: create.isPending || update.isPending,
    /** The database the step would advance with, when it has the settings its engine needs. */
    selected,
    /** What the step machine counts as this step's database: nothing while the form is open. */
    progressDatabase: editor == null ? selected : undefined,
    canAdvance: editor == null && hasDatabaseDetails(selected),
    select: (id: string) => onSelect(id),
    startAdd: () => {
      void track('sqlImports', 'addDatabaseStarted', eventProperties);
      openEditor({ mode: 'add' });
    },
    startEdit: (database: SqlImportDatabase) => {
      void track('sqlImports', 'editDatabaseStarted', eventProperties);
      openEditor({ mode: 'edit', database });
    },
    cancelEdit: () => {
      setConnectionError(null);
      setEditor(null);
    },
    saveDatabase,
    confirmDelete: (database: SqlImportDatabase) =>
      void confirm({
        title: 'Delete source',
        message: <DeleteDatabaseMessage name={databaseTitle(database)} />,
        cancelLabel: 'Cancel',
        confirmLabel: 'Delete source',
        isCritical: true,
        onConfirm: () => deleteDatabase(database),
      }),
  };
}

export type DatabaseStepState = ReturnType<typeof useDatabaseStep>;

type UseWizardDatabaseArgs = Omit<UseDatabaseStepArgs, 'selectedId' | 'onSelect'> & {
  draft: SqlImportDraft;
  setDraftFields: (changes: Partial<SqlImportDraft>) => void;
};

/** The Database step bound to the wizard's draft: the draft's `database_id` is the selection. */
export function useWizardDatabase({ draft, setDraftFields, ...args }: UseWizardDatabaseArgs) {
  const databaseStep = useDatabaseStep({
    ...args,
    selectedId: draft.database_id,
    onSelect: (id) => setDraftFields({ database_id: id }),
  });
  return { databaseStep, database: databaseStep.selected };
}
